import { CopyOutlined } from "@ant-design/icons";
import { App, Dropdown, Flex, MenuProps } from "antd";
import { FC, SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useProtectedUserRoleSelector } from "@/hooks/redux";
import { isManagerRole } from "@/utils/business/isManagerRole";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { QrModal } from "./QRModal";
import {
    StyledMBAboutPointButton,
    StyledMBCopyButton,
    StyledMBGeoPin,
    StyledMBPointMenuController,
    StyledMBQRIcon,
} from "./Styled";

interface PointActionsBarProps {
    pointId: string;
    pointName: string;
    geo: string | null;
    address: string;
    menu?: MenuProps;
    objToCopy: { [key: string]: unknown };
}

export const PointActionsBar: FC<PointActionsBarProps> = ({
    pointId,
    menu,
    geo,
    address,
    pointName,
    objToCopy,
}) => {
    const { notification } = App.useApp();

    const role = useProtectedUserRoleSelector();
    const [qrModalOpen, setQRModalOpen] = useState<boolean>(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const isManagerNow = isManagerRole(role);

    const handleOpenPoint = (e: SyntheticEvent) => {
        e.preventDefault();
        const currentAddress = geo || address;
        window.open(
            `https://www.google.com/maps/place/${currentAddress?.replaceAll(
                " ",
                "+",
            )}`,
        );
    };

    const handleCopyPoint = () => {
        copyObjectToClipboard(objToCopy, notification);
    };

    const handleAboutPoint = useCallback(
        () =>
            navigate(
                buildRoutePath(ROUTES.PointDetails, {
                    pointId,
                }),
            ),
        [pointId, navigate],
    );

    const patchedMenu = useMemo(() => {
        if (!menu) return undefined;
        return {
            ...menu,
            items: menu.items?.map((item) => {
                if (!item) return item;
                const typedItem = item as typeof item & {
                    closeDropdown?: boolean;
                    onClick?: () => void;
                };
                const { closeDropdown, ...restItem } = typedItem;
                if (closeDropdown) {
                    return {
                        ...restItem,
                        onClick: () => {
                            typedItem.onClick?.();
                            setIsDropdownOpen(false);
                        },
                    };
                }
                return restItem;
            }),
        };
    }, [menu]);

    return (
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
            <Flex justify="flex-end" align="center" gap="12px">
                <StyledMBAboutPointButton onClick={handleAboutPoint}>
                    Про ТТ
                </StyledMBAboutPointButton>
                {isManagerNow && (
                    <StyledMBQRIcon onClick={() => setQRModalOpen(true)} />
                )}
                <StyledMBGeoPin onClick={handleOpenPoint} />

                <StyledMBCopyButton
                    shape="circle"
                    icon={<CopyOutlined />}
                    onClick={handleCopyPoint}
                />
            </Flex>

            {patchedMenu?.items && patchedMenu?.items?.length > 0 && (
                <Dropdown
                    open={isDropdownOpen}
                    onOpenChange={(nextOpen, info) => {
                        if (info?.source === "trigger" || nextOpen) {
                            setIsDropdownOpen(nextOpen);
                        }
                    }}
                    menu={patchedMenu}
                    trigger={["click"]}
                >
                    <StyledMBPointMenuController />
                </Dropdown>
            )}

            {isManagerNow && (
                <QrModal
                    open={qrModalOpen}
                    pointId={pointId}
                    pointName={pointName}
                    onClose={() => setQRModalOpen(false)}
                />
            )}
        </Flex>
    );
};
